// 🎯 Single promo engine shared by POS cart, promo posters and any future caller.
// Pure functions — no React, no axios — so they are unit-tested (see promoEngine.test.js).

export const ruleTimeOk = (rule, now = new Date()) => {
  if (rule.start_date && now < new Date(rule.start_date + "T00:00:00")) return false;
  if (rule.end_date && now > new Date(rule.end_date + "T23:59:59")) return false;
  if (Array.isArray(rule.days) && rule.days.length > 0 && !rule.days.includes(now.getDay())) return false;
  if (rule.start_time && rule.end_time) {
    const hm = now.getHours() * 60 + now.getMinutes();
    const [sh, sm] = rule.start_time.split(":").map(Number);
    const [eh, em] = rule.end_time.split(":").map(Number);
    const s = sh * 60 + (sm || 0), e = eh * 60 + (em || 0);
    if (s <= e ? (hm < s || hm > e) : (hm < s && hm > e)) return false;
  }
  return true;
};

export const ruleScopeOk = (rule, p) =>
  !rule.apply_to || rule.apply_to === "all" || (rule.product_ids || []).includes(p.id) || (rule.category_ids || []).includes(p.category_id || p.category);

/** Discount ($) for ONE unit of a product at unit price `unitUsd` under `rule`. 0 if not applicable. */
export const unitPromoDiscount = (rule, unitUsd) => {
  if (rule.type === "discount") return unitUsd * ((parseFloat(rule.discount_value) || 0) / 100);
  if (rule.type === "flash_sale") { const fp = parseFloat(rule.flash_price) || 0; return fp > 0 && unitUsd > fp ? unitUsd - fp : 0; }
  return 0;
};

/**
 * Cart-level auto promo. `items` = [{ units, unitPrice, product }] where unitPrice is the SAME $ basis the
 * cart displays (Bs ÷ tasa in Bs mode). Returns { amount, label }.
 */
export function computeAutoPromo({ items, rules, now = new Date() }) {
  let amount = 0; const names = new Set();
  // perItem[idx] = $ discount attributed to items[idx] — lets the POS write promo prices INTO each
  // order line so server totals, fiscal printer, A4 and order lookup all agree with what was charged.
  const perItem = items.map(() => 0);
  const total = items.reduce((s, i) => s + i.units * i.unitPrice, 0);
  const idxFor = (rule) => items.map((i, idx) => (ruleScopeOk(rule, i.product || {}) ? idx : -1)).filter(idx => idx >= 0);
  for (const rule of rules) {
    if (rule.status !== "active" || !ruleTimeOk(rule, now)) continue;
    const line = items.map(() => 0);
    if (rule.type === "second_half" || rule.type === "second_unit") {
      const pct = parseFloat(rule.second_percent ?? rule.percent) || 50;
      for (const idx of idxFor(rule)) { const i = items[idx]; line[idx] += Math.floor(i.units / 2) * i.unitPrice * pct / 100; }
    } else if (rule.type === "discount") {
      if ((parseFloat(rule.min_amount) || 0) > total) continue;
      for (const idx of idxFor(rule)) { const i = items[idx]; line[idx] += i.units * unitPromoDiscount(rule, i.unitPrice); }
    } else if (rule.type === "buy_get") {
      const buy = parseInt(rule.buy_qty) || 2, get = parseInt(rule.get_qty) || 1;
      for (const idx of idxFor(rule)) { const i = items[idx]; line[idx] += Math.floor(i.units / (buy + get)) * get * i.unitPrice; }
    } else if (rule.type === "flash_sale") {
      for (const idx of idxFor(rule)) { const i = items[idx]; line[idx] += i.units * unitPromoDiscount(rule, i.unitPrice); }
    } else if (rule.type === "full_reduction") {
      const minA = parseFloat(rule.min_amount) || 0;
      if (minA > 0 && total >= minA) spreadProportional(parseFloat(rule.discount_value) || 0, items, line);
    }
    const d = line.reduce((s, v) => s + v, 0);
    if (d > 0) { amount += d; names.add(rule.name || "Promo"); line.forEach((v, idx) => { perItem[idx] += v; }); }
  }
  // Full precision: rounding to cents here distorts cheap items (8% of $0.757 = 0.0606 → 0.06 = 7.9%) and
  // makes the Bs total differ from the printed poster. Callers round only what they display/store.
  return { amount, label: Array.from(names).join(" + "), perItem };
}

/** Unit price for a promo line: 2 decimals when unit × qty reproduces the net line (±½¢), else 4 decimals (buy 2 get 1 → 0.6667). */
export function promoUnit(netLine, qty) {
  if (!(qty > 0)) return 0;
  const x = netLine / qty;
  const u2 = Math.round(x * 100) / 100;
  return Math.abs(u2 * qty - netLine) < 0.005 ? u2 : Math.round(x * 10000) / 10000;
}

/**
 * Bs to deduct for an order-level promo when printing. New orders (promo_in_items) carry the promo in
 * their lines → 0. Legacy orders: prefer the exact Bs the server recorded (gross − total_amount_bs)
 * because promo_discount is rounded to cents ($0.05 × 871,37 = 43,57 while the customer paid 46,04 less).
 */
export function legacyPromoDeductBs(order, grossBs, rate) {
  if (order?.promo_in_items) return 0;
  const promoBs = (parseFloat(order?.promo_discount) || 0) * (rate || 0);
  if (!(promoBs > 0)) return 0;
  const srvBs = parseFloat(order?.total_amount_bs) || 0;
  const pts = parseFloat(order?.points_discount) || 0;
  if (!pts && srvBs > 0 && srvBs < grossBs - 0.01 && Math.abs(grossBs - srvBs - promoBs) < Math.max(5, promoBs * 0.2)) return grossBs - srvBs;
  return promoBs;
}

/** Bs the customer saved, for the "USTED AHORRO" line: exact per-line figure for new orders, deducted figure for legacy. */
export function promoSavedBs(order, deductBs, rate) {
  const promoBs = (parseFloat(order?.promo_discount) || 0) * (rate || 0);
  if (!(promoBs > 0)) return 0;
  if (!order?.promo_in_items) return deductBs || promoBs;
  const fromLines = (order.items || []).reduce((s, it) => {
    const lp = parseFloat(it.list_price_bs) || 0, up = parseFloat(it.unit_price_bs) || 0;
    return s + (lp > up ? (lp - up) * (parseFloat(it.quantity) || 1) : 0);
  }, 0);
  return fromLines > 0 ? fromLines : promoBs;
}

/** Spread an order-level $ discount across lines in proportion to their value (never above the line value). */
export function spreadProportional(discount, items, into) {
  const total = items.reduce((s, i) => s + i.units * i.unitPrice, 0);
  if (!(discount > 0) || !(total > 0)) return into;
  const d = Math.min(discount, total);
  items.forEach((i, idx) => { into[idx] = (into[idx] || 0) + d * (i.units * i.unitPrice) / total; });
  return into;
}

/**
 * Bs the POS will actually charge for one unit after a promo. Bs-priced products (stored price_bs1):
 * Bs = stored Bs − ($ discount × tasa) — exactly bsFinalTotalPreIva in PosPage. $-priced: $ × tasa.
 */
export function promoBsForProduct({ priceBs1, baseUsd, promoUsd, rate }) {
  const b1 = parseFloat(priceBs1) || 0;
  if (!(rate > 1)) return 0;
  if (b1 > 0 && baseUsd > 0) return Math.round((b1 - (baseUsd - promoUsd) * rate) * 100) / 100;
  return Math.round(promoUsd * rate * 100) / 100;
}

/** Bs display: whole numbers stay whole, otherwise 2 decimals (matches the receipt). */
export const fmtBs = (v) => Number.isInteger(v) ? v.toLocaleString("es-VE") : v.toLocaleString("es-VE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
export function promoPriceForProduct(rule, baseUsd) {
  if (!rule || !(baseUsd > 0)) return null;
  const d = unitPromoDiscount(rule, baseUsd);
  return d > 0 ? baseUsd - d : null;
}
